/// <reference path="./draw.d.ts" />
import { PageGen, PdfRect, ETextAlign, PdfFont, PdfSize } from '../pdf/pdf';
import { ModuleContent, ModuleParams } from './types';
import { LabelContent } from './dot/types';
import { moduleRegistry } from './registry';
import { parseModuleValue } from './registry';

/**
 * Draw module content (either plain text or embedded module)
 */
PageGen.prototype.drawContent = function (
  content: string | ModuleContent | LabelContent,
  rect: PdfRect,
  align: ETextAlign,
  params: ModuleParams
): void {
  // Handle LabelContent type
  if (content && typeof content === 'object' && 'type' in content) {
    const labelContent = content as LabelContent;
    if (labelContent.type === 'string') {
      // LabelContent string type
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
      this.drawString(labelContent.value, font, params.brush || PdfBrush.Black, null, rect, align);
      return;
    } else if (labelContent.type === 'module') {
      // LabelContent module type
      const moduleContent = labelContent.value as ModuleContent;
      const module = moduleRegistry.get(moduleContent.type);

      if (module) {
        module.draw(moduleContent, rect, align, params);
      } else {
        const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
        this.drawString(
          `[${moduleContent.type} module]`,
          font,
          params.brush || PdfBrush.Black,
          null,
          rect,
          align
        );
      }
      return;
    }
  }

  if (typeof content === 'string') {
    // Parse the content to check if it's a module tag
    const parsed = parseModuleValue(content);

    if (parsed.type === 'module') {
      // It's a module - draw it
      const moduleContent = parsed.value as ModuleContent;
      const module = moduleRegistry.get(moduleContent.type);

      if (module) {
        module.draw(moduleContent, rect, align, params);
      } else {
        // Fallback to plain text
        const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
        this.drawString(content, font, params.brush || PdfBrush.Black, null, rect, align);
      }
    } else {
      // Plain text
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
      this.drawString(
        parsed.value as string,
        font,
        params.brush || PdfBrush.Black,
        null,
        rect,
        align
      );
    }
  } else if (content && typeof content === 'object' && 'type' in content && 'attrs' in content) {
    // Already parsed module content (ModuleContent)
    const moduleContent = content;
    const module = moduleRegistry.get(moduleContent.type);

    if (module) {
      module.draw(moduleContent, rect, align, params);
    } else {
      // Fallback
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
      this.drawString(
        `[${moduleContent.type} module]`,
        font,
        params.brush || PdfBrush.Black,
        null,
        rect,
        align
      );
    }
  } else {
    // Unknown type
    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
    this.drawString('[unknown content]', font, params.brush || PdfBrush.Black, null, rect, align);
  }
};

/**
 * Measure module content (either plain text or embedded module)
 */
PageGen.prototype.measureContent = function (
  content: string | ModuleContent | LabelContent,
  params: ModuleParams
): PdfSize {
  // Handle LabelContent type
  if (content && typeof content === 'object' && 'type' in content) {
    const labelContent = content as LabelContent;
    if (labelContent.type === 'string') {
      // LabelContent string type
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
      return this.measureString(labelContent.value, font);
    } else if (labelContent.type === 'module') {
      // LabelContent module type
      const moduleContent = labelContent.value as ModuleContent;
      const module = moduleRegistry.get(moduleContent.type);

      if (module) {
        return module.measure(moduleContent, params);
      } else {
        const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
        return this.measureString(`[${moduleContent.type} module]`, font);
      }
    }
  }

  if (typeof content === 'string') {
    // Parse the content to check if it's a module tag
    const parsed = parseModuleValue(content);

    if (parsed.type === 'module') {
      // It's a module - measure it
      const moduleContent = parsed.value as ModuleContent;
      const module = moduleRegistry.get(moduleContent.type);

      if (module) {
        return module.measure(moduleContent, params);
      } else {
        // Fallback to plain text measurement
        const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
        return this.measureString(content, font);
      }
    } else {
      // Plain text
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
      return this.measureString(parsed.value as string, font);
    }
  } else if (content && typeof content === 'object' && 'type' in content && 'attrs' in content) {
    // Already parsed module content (ModuleContent)
    const moduleContent = content;
    const module = moduleRegistry.get(moduleContent.type);

    if (module) {
      return module.measure(moduleContent, params);
    } else {
      // Fallback
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
      return this.measureString(`[${moduleContent.type} module]`, font);
    }
  } else {
    // Unknown type
    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
    return this.measureString('[unknown content]', font);
  }
};

/**
 * Draw a module by name and content string
 */
PageGen.prototype.drawModule = function (
  moduleName: string,
  content: string,
  rect: PdfRect,
  align: ETextAlign,
  params: ModuleParams
): void {
  const module = moduleRegistry.get(moduleName);

  if (module) {
    const moduleContent = module.parse(content, {});
    module.draw(moduleContent, rect, align, params);
  } else {
    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 12);
    this.drawString(
      `[${moduleName} not found]`,
      font,
      params.brush || PdfBrush.Black,
      null,
      rect,
      align
    );
  }
};

// Import PdfBrush for fallback cases
import { PdfBrush } from '../pdf/pdf';
